import { CopyOverlay } from '@/shared/components/CopyOverlay'

// ── Minimal HCL token types ───────────────────────────────────────────────────

type Token = { kind: 'keyword' | 'block' | 'attr' | 'string' | 'url' | 'comment' | 'punct' | 'plain'; text: string }

const KEYWORDS = new Set(['terraform', 'backend', 'required_providers', 'provider'])

/**
 * Tokenises a single line of HCL into styled spans.
 * Handles: block keywords, attribute keys, quoted strings (with URL detection inside),
 * `=` punctuation, `{` / `}`, and `#` comments.
 */
function tokeniseLine(line: string): Token[] {
  const tokens: Token[] = []
  let rest = line

  // Comment
  if (/^\s*#/.test(rest)) {
    tokens.push({ kind: 'comment', text: rest })
    return tokens
  }

  // Leading whitespace
  const indent = rest.match(/^(\s*)/)![1]
  if (indent) { tokens.push({ kind: 'plain', text: indent }); rest = rest.slice(indent.length) }

  // Block open: `keyword "label" {` or `keyword {`
  const blockMatch = rest.match(/^([a-zA-Z_]\w*)(\s+"[^"]*")?(\s*\{.*)$/)
  if (blockMatch) {
    const kw = blockMatch[1]
    tokens.push({ kind: KEYWORDS.has(kw) ? 'keyword' : 'block', text: kw })
    if (blockMatch[2]) {
      const lbl = blockMatch[2]
      const spaceEnd = lbl.match(/^(\s*)/)![1]
      tokens.push({ kind: 'plain', text: spaceEnd })
      tokens.push({ kind: 'block', text: lbl.slice(spaceEnd.length) })
    }
    tokens.push({ kind: 'punct', text: blockMatch[3] })
    return tokens
  }

  // Closing brace
  if (/^}$/.test(rest)) {
    tokens.push({ kind: 'punct', text: rest })
    return tokens
  }

  // Attribute: `key = "value"` or `key = value`
  const attrMatch = rest.match(/^([a-zA-Z_]\w*)\s*(=)\s*(.*)$/)
  if (attrMatch) {
    tokens.push({ kind: 'attr', text: attrMatch[1] })
    tokens.push({ kind: 'plain', text: rest.slice(attrMatch[1].length, rest.indexOf('=') + 1) })
    const val = attrMatch[3]
    if (val.startsWith('"')) {
      // Split string into url and non-url parts
      tokens.push(...tokeniseString(val))
    } else {
      tokens.push({ kind: 'plain', text: ' ' + val })
    }
    return tokens
  }

  tokens.push({ kind: 'plain', text: rest })
  return tokens
}

function tokeniseString(raw: string): Token[] {
  // raw includes surrounding quotes, e.g. `"https://foo/bar"`
  const inner = raw.slice(1, raw.endsWith('"') ? raw.length - 1 : undefined)
  const urlRx = /https?:\/\/[^\s"<>{}|\\^[\]`]*/g
  const parts: Token[] = [{ kind: 'punct', text: '"' }]
  let last = 0
  let m: RegExpExecArray | null
  while ((m = urlRx.exec(inner)) !== null) {
    if (m.index > last) parts.push({ kind: 'string', text: inner.slice(last, m.index) })
    parts.push({ kind: 'url', text: m[0] })
    last = m.index + m[0].length
  }
  if (last < inner.length) parts.push({ kind: 'string', text: inner.slice(last) })
  if (raw.endsWith('"')) parts.push({ kind: 'punct', text: '"' })
  return parts
}

const TOKEN_COLORS: Record<Token['kind'], string> = {
  keyword: 'var(--accent)',   // purple  - terraform, backend
  block:   'var(--text2)',    // subdued - block label e.g. "http"
  attr:    'var(--text2)',    // subdued - attribute keys (readable but not competing)
  string:  'var(--green)',    // green   - quoted string values
  url:     'var(--text)',     // bright  - URLs are the key content
  comment: 'var(--text3)',    // dim     - comments
  punct:   'var(--text3)',    // dim     - braces, quotes, =
  plain:   'var(--text3)',    // dim     - spaces / operators
}

function Line({ line }: { line: string }) {
  const tokens = tokeniseLine(line)
  return (
    <span style={{ display: 'block' }}>
      {tokens.map((t, i) => (
        <span key={i} style={{ color: TOKEN_COLORS[t.kind] }}>{t.text}</span>
      ))}
    </span>
  )
}

// ── Public component ──────────────────────────────────────────────────────────

interface HclSnippetProps {
  /** The full HCL text to render */
  code: string
  /** Plain text written to clipboard on copy (can differ from rendered code) */
  copyText?: string
  fontSize?: string
}

export function HclSnippet({ code, copyText, fontSize = '0.78rem' }: HclSnippetProps) {
  const lines = code.split('\n')

  return (
    <div style={{ position: 'relative' }}>
      <pre style={{
        fontFamily: 'var(--mono)',
        fontSize,
        lineHeight: 1.7,
        background: 'var(--bg4)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        padding: '14px 16px',
        margin: 0,
        overflowX: 'auto',
        textAlign: 'left',
      }}>
        {lines.map((line, i) => <Line key={i} line={line} />)}
      </pre>
      <CopyOverlay value={copyText ?? code} label="Copy" />
    </div>
  )
}

// ── Backend config builder ────────────────────────────────────────────────────

export function buildBackendHcl(_orgId: string, workspace: string, orgSlug?: string, token?: string | null): string {
  const baseWorkspace = `https://registry.terramantle.dev/state/${orgSlug}/${workspace}`
  const orgComment = orgSlug ? `# org: ${orgSlug}\n` : ''
  const tokenVal = token ?? '<bot token>'
  return `
${orgComment}
# This is your actual access token, which you can use
# to test before running Terraform/OpenTofu - remember it will expire.

# export TF_HTTP_PASSWORD=${tokenVal}

terraform {
  backend "http" {
    address        = "${baseWorkspace}"
    lock_address   = "${baseWorkspace}.lock"
    unlock_address = "${baseWorkspace}.lock"
    username       = "terramantle-bot"
  }
}
`
}
